import { useEffect, useState } from 'react'
import { Alert, Col, Container, Form, Row, Tab, Nav } from 'react-bootstrap'
import KLayerNavigation from '../components/KLayerNavigation'
import KButton from '../components/KButton'
import { isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'

interface ConnectionStatus {
  connected: boolean
  expired: boolean
  expiresAt: string | null
  tokenDurationMinutes: number
}

export default function AbacusSettings() {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null)
  const [serverUrl, setServerUrl] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ created: number; updated: number } | null>(
    null
  )
  const [mandantId, setMandantId] = useState('')

  useEffect(() => {
    fetchConnectionStatus()
  }, [])

  const fetchConnectionStatus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/abacus/connection/status`)
      const data = await res.json()
      setConnectionStatus(data)
    } catch {
      setConnectionStatus(null)
    }
  }

  const formatExpiresAt = (isoDate: string) => {
    return new Date(isoDate).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const saveConnection = async () => {
    if (!serverUrl.trim() || !clientId.trim() || !clientSecret.trim()) {
      showKAlert('danger', t('abacusCredentialsRequired'))
      return
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/abacus/connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serverUrl: serverUrl.trim(),
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
        }),
      })
      const data = await res.json()
      if (data.ok) {
        setClientSecret('')
        fetchConnectionStatus()
      }
    } catch {
      showKAlert('danger', t('abacusConnectionFailed'))
    }
  }

  const importClients = async () => {
    if (!mandantId.trim()) {
      showKAlert('danger', t('abacusMandantIdRequired'))
      return
    }
    setImporting(true)
    setImportResult(null)
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/abacus/import/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mandantId: mandantId.trim() }),
      })
      const data = await res.json()
      if (data.ok) {
        setImportResult({ created: data.created, updated: data.updated })
      }
    } catch {
      showKAlert('danger', t('abacusImportFailed'))
    } finally {
      setImporting(false)
    }
  }

  const disconnectAbacus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/abacus/connection`, {
        method: 'DELETE',
      })
      const data = await res.json()
      if (data.ok) {
        setConnectionStatus({
          connected: false,
          expired: false,
          expiresAt: null,
          tokenDurationMinutes: 0,
        })
      }
    } catch {
      showKAlert('danger', t('abacusDisconnectFailed'))
    }
  }

  const reconnectAbacus = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/abacus/reconnect`, {
        method: 'POST',
      })
      const data = await res.json()
      if (data.ok) {
        fetchConnectionStatus()
      }
    } catch {
      showKAlert('danger', t('abacusReconnectFailed'))
    }
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='ms-sm-auto col-lg-10 px-md-4 md-4'>
          <div className='col-12'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {t('abacusIntegration')}
            </p>
          </div>

          <div className='col-12 my-5'>
            <Tab.Container defaultActiveKey='credentials'>
              <Nav variant='tabs' className='mb-4'>
                <Nav.Item>
                  <Nav.Link eventKey='credentials'>{t('abacusClientCredentials')}</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey='credentials'>
                  {connectionStatus && connectionStatus.connected && (
                    <Row className='mb-4'>
                      <Col lg={{ span: 8, offset: 4 }}>
                        <Alert
                          variant={connectionStatus.expired ? 'danger' : 'info'}
                          className='mb-0 d-flex justify-content-between align-items-center'
                        >
                          <div>
                            {connectionStatus.expired ? (
                              <>
                                <i className='bi bi-exclamation-triangle-fill me-2'></i>
                                {t('abacusTokenExpired')}{' '}
                                {t('abacusTokenDuration').replace(
                                  '{minutes}',
                                  String(connectionStatus.tokenDurationMinutes)
                                )}
                              </>
                            ) : (
                              <>
                                <i className='bi bi-check-circle-fill me-2'></i>
                                {t('abacusTokenActive')}
                                {connectionStatus.expiresAt && (
                                  <>
                                    {' '}
                                    — {t('abacusTokenExpiresAt')}{' '}
                                    <strong>{formatExpiresAt(connectionStatus.expiresAt)}</strong>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                          <div className='d-flex gap-2'>
                            {connectionStatus.expired && (
                              <KButton
                                id='btn-abacus-reconnect'
                                label={t('abacusReconnect')}
                                variant='outline-primary'
                                icon='arrow-repeat'
                                size='sm'
                                onClick={reconnectAbacus}
                              />
                            )}
                            <KButton
                              id='btn-abacus-disconnect'
                              label={t('abacusDisconnect')}
                              variant='outline-danger'
                              icon='x-circle'
                              size='sm'
                              onClick={disconnectAbacus}
                            />
                          </div>
                        </Alert>
                      </Col>
                    </Row>
                  )}
                  {connectionStatus && !connectionStatus.connected && (
                    <Row>
                      <Col lg={4}>
                        <div className='d-flex justify-between section-title-blade'>
                          <div className='pe-4 sm:px-0'>
                            <p className='mt-1 text-draft'>{t('abacusConnectionSetup')}</p>
                          </div>
                        </div>
                      </Col>
                      <Col lg={8}>
                        <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                          <p className='text-muted mb-3'>{t('abacusCredentialsDescription')}</p>
                          <Form>
                            <Form.Group className='mb-3'>
                              <Form.Label className='fw-medium smaller text-primary'>
                                {t('abacusServerUrl')}
                              </Form.Label>
                              <Form.Control
                                type='url'
                                placeholder={t('abacusServerUrlPlaceholder')}
                                value={serverUrl}
                                onChange={e => setServerUrl(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className='mb-3'>
                              <Form.Label className='fw-medium smaller text-primary'>
                                {t('abacusClientId')}
                              </Form.Label>
                              <Form.Control
                                type='text'
                                placeholder={t('abacusClientIdPlaceholder')}
                                value={clientId}
                                onChange={e => setClientId(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className='mb-3'>
                              <Form.Label className='fw-medium smaller text-primary'>
                                {t('abacusClientSecret')}
                              </Form.Label>
                              <Form.Control
                                type='password'
                                placeholder={t('abacusClientSecretPlaceholder')}
                                value={clientSecret}
                                onChange={e => setClientSecret(e.target.value)}
                              />
                            </Form.Group>
                            <KButton
                              id='btn-abacus-save'
                              label={t('abacusSaveConnection')}
                              variant='primary'
                              icon='plug'
                              onClick={saveConnection}
                            />
                          </Form>
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Row className='mt-4'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <p className='mt-1 text-draft'>{t('abacusImportClients')}</p>
                        </div>
                      </div>
                    </Col>
                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        <p className='text-muted mb-3'>{t('abacusImportDescription')}</p>
                        <Form.Group className='mb-3'>
                          <Form.Label className='fw-medium smaller text-primary'>
                            {t('abacusMandantId')}
                          </Form.Label>
                          <Form.Control
                            type='text'
                            placeholder={t('abacusMandantIdPlaceholder')}
                            value={mandantId}
                            onChange={e => setMandantId(e.target.value)}
                          />
                        </Form.Group>
                        <KButton
                          id='btn-abacus-import'
                          label={importing ? t('abacusImporting') : t('abacusImportClients')}
                          variant='primary'
                          icon='download'
                          disabled={importing}
                          onClick={importClients}
                        />
                        {importResult && (
                          <p className='text-success mt-3 mb-0'>
                            {t('abacusImportResult')}: {importResult.created} {t('abacusCreated')},{' '}
                            {importResult.updated} {t('abacusUpdated')}
                          </p>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </main>
      </Row>
    </Container>
  )
}
